
export type OAuthErrorCategory = 'network' | 'auth' | 'api' | 'timeout' | 'security' | 'user' | 'unknown';

export interface CategorizedError {
  category: OAuthErrorCategory;
  code: string;
  message: string;
  userMessage: string;
  actionable: string;
  canRetry: boolean;
  retryAfter?: number; // seconds
}

export interface CircuitBreakerState {
  failures: number;
  lastFailure: number;
  state: 'closed' | 'open' | 'half-open';
}

const CIRCUIT_BREAKER_KEY = 'oauth_circuit_breaker';
const FAILURE_THRESHOLD = 3;
const RECOVERY_TIMEOUT = 60000; // 1 minute
const HALF_OPEN_MAX_CALLS = 1;

export const circuitBreaker = {
  getState(): CircuitBreakerState {
    try {
      const stored = localStorage.getItem(CIRCUIT_BREAKER_KEY);
      if (!stored) {
        return { failures: 0, lastFailure: 0, state: 'closed' };
      }
      return JSON.parse(stored);
    } catch {
      return { failures: 0, lastFailure: 0, state: 'closed' };
    }
  },

  setState(state: CircuitBreakerState): void {
    try {
      localStorage.setItem(CIRCUIT_BREAKER_KEY, JSON.stringify(state));
    } catch {
      // Ignore storage errors
    }
  },

  canExecute(): boolean {
    const state = this.getState();
    const now = Date.now();

    switch (state.state) {
      case 'closed':
        return true;
      case 'open':
        if (now - state.lastFailure > RECOVERY_TIMEOUT) {
          this.setState({ ...state, state: 'half-open' });
          return true;
        }
        return false;
      case 'half-open':
        return true;
      default:
        return true;
    }
  },

  recordSuccess(): void {
    this.setState({ failures: 0, lastFailure: 0, state: 'closed' });
  },

  recordFailure(): void {
    const state = this.getState();
    const now = Date.now();
    const newFailures = state.failures + 1;

    if (newFailures >= FAILURE_THRESHOLD) {
      this.setState({
        failures: newFailures,
        lastFailure: now,
        state: 'open'
      });
    } else {
      this.setState({
        failures: newFailures,
        lastFailure: now,
        state: state.state
      });
    }
  },

  reset(): void {
    localStorage.removeItem(CIRCUIT_BREAKER_KEY);
  }
};

export const categorizeOAuthError = (error: any): CategorizedError => {
  const errorMessage = error?.message || String(error);
  const errorCode = error?.code || error?.status;
  
  // Network errors with specific codes
  if (errorMessage.includes('fetch') || errorMessage.includes('network') || errorCode === 'NETWORK_ERROR') {
    return {
      category: 'network',
      code: 'NET_001',
      message: errorMessage,
      userMessage: 'Network connection error',
      actionable: 'Check your internet connection and try again',
      canRetry: true,
      retryAfter: 5
    };
  }
  
  // Connection timeout
  if (errorMessage.includes('timeout') || errorMessage.includes('TIMEOUT')) {
    return {
      category: 'timeout',
      code: 'TMO_001',
      message: errorMessage,
      userMessage: 'Connection timeout',
      actionable: 'The connection took too long. Please try again',
      canRetry: true,
      retryAfter: 10
    };
  }
  
  // Authentication errors
  if (errorMessage.includes('unauthorized') || errorCode === 401 || errorMessage.includes('authentication failed')) {
    return {
      category: 'auth',
      code: 'AUTH_001',
      message: errorMessage,
      userMessage: 'Authentication failed',
      actionable: 'Please try connecting to cTrader again',
      canRetry: true,
      retryAfter: 0
    };
  }
  
  // API errors with specific handling
  if (errorCode >= 400 && errorCode < 500) {
    const code = errorCode === 429 ? 'API_429' : 'API_4XX';
    const retryAfter = errorCode === 429 ? 60 : 30;
    
    return {
      category: 'api',
      code,
      message: errorMessage,
      userMessage: errorCode === 429 ? 'Rate limit exceeded' : 'API request failed',
      actionable: errorCode === 429 ? 'Please wait before trying again' : 'There was an issue with the cTrader API. Please try again',
      canRetry: true,
      retryAfter
    };
  }
  
  // Server errors
  if (errorCode >= 500) {
    return {
      category: 'api',
      code: 'API_5XX',
      message: errorMessage,
      userMessage: 'Server error',
      actionable: 'cTrader servers are experiencing issues. Please try again later',
      canRetry: true,
      retryAfter: 120
    };
  }
  
  // Security errors
  if (errorMessage.includes('CSRF') || errorMessage.includes('Invalid state') || errorMessage.includes('expired')) {
    return {
      category: 'security',
      code: 'SEC_001',
      message: errorMessage,
      userMessage: 'Security validation failed',
      actionable: 'Please start the connection process again',
      canRetry: false
    };
  }
  
  // User cancellation
  if (errorMessage.includes('cancelled') || errorMessage.includes('denied')) {
    return {
      category: 'user',
      code: 'USR_001',
      message: errorMessage,
      userMessage: 'Connection cancelled',
      actionable: 'You can try connecting again when ready',
      canRetry: true,
      retryAfter: 0
    };
  }
  
  // Circuit breaker triggered
  if (errorMessage.includes('Circuit breaker')) {
    return {
      category: 'network',
      code: 'CB_001',
      message: errorMessage,
      userMessage: 'Service temporarily unavailable',
      actionable: 'Multiple failures detected. Please wait a moment before trying again',
      canRetry: true,
      retryAfter: 60
    };
  }
  
  // Unknown errors
  return {
    category: 'unknown',
    code: 'UNK_001',
    message: errorMessage,
    userMessage: 'An unexpected error occurred',
    actionable: 'Please try again or contact support if the issue persists',
    canRetry: true,
    retryAfter: 30
  };
};

export const getRetryDelay = (attemptNumber: number, errorCategory?: OAuthErrorCategory): number => {
  // Base delays by error category (in milliseconds)
  const baseDelays = {
    network: 2000,
    timeout: 5000,
    auth: 1000,
    api: 3000,
    security: 0, // No retry for security errors
    user: 0, // No automatic retry for user actions
    unknown: 2000
  };
  
  const baseDelay = errorCategory ? baseDelays[errorCategory] : 2000;
  
  // Exponential backoff with jitter
  const exponential = baseDelay * Math.pow(2, attemptNumber - 1);
  const jitter = Math.random() * 1000; // Add up to 1s jitter
  
  // Cap at 30 seconds
  return Math.min(exponential + jitter, 30000);
};

export const shouldRetry = (error: CategorizedError, attemptNumber: number): boolean => {
  // Don't retry security or user errors
  if (!error.canRetry) return false;
  
  // Don't exceed max attempts
  if (attemptNumber >= 3) return false;
  
  // Check circuit breaker
  if (!circuitBreaker.canExecute()) return false;
  
  return true;
};
